import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, EducationContent, QuestionConfigQuestionType } from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import WaterTreatment from '/business/airbp/images/icons/WaterTreatment.svg';
import { initiativeVisuals } from '/business/shared/components/questions/helper/initiativeVisuals';
import useOnSiteWaterTreatmentSchema from '/business/shared/components/questions/scope1/_hooks/useOnSiteWaterTreatmentSchema';

import useFormatSharedSupplierScope1 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1';
import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import initiativeBackground from '/business/shared/images/initiatives/water-treatment-banner-1x.png';
import initiativeCardBackground from '/business/shared/images/initiatives/on-site-water-initiative-1x.png';
import educationContentJson from '/business/shared/components/questions/scope1/_education/water-treatment.json';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';

interface ConfigureOnSiteWaterTreatmentOptions {
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
}

type FuelQuantityRow = {
  fuelUnit?: string;
  harmonisedInjection: (fuelUnit: string) => string;
  quantity: number;
};

type FuelTonnageRow = {
  tonnage: number;
};

function isEducationContent(value: object): value is EducationContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'topParas' in value &&
    Array.isArray(value.topParas) &&
    'exampleParas' in value &&
    Array.isArray(value.exampleParas)
  );
}

function getEducationContent(): EducationContent {
  if (!isEducationContent(educationContentJson)) {
    throw new Error('On-site water treatment education content is not configured');
  }
  return educationContentJson;
}

export const getConfigureOnSiteWaterTreatment = ({
  i18n,
  reductionCardsSlides = ['slide1', 'slide2', 'slide3', 'slide4'],
}: ConfigureOnSiteWaterTreatmentOptions): QuestionConfigQuestionType => {
  return {
    id: 'on-site-water-treatment',
    useHookLoader: useOnSiteWaterTreatmentSchema,
    scopeCategory: questionScopeCategories.SCOPE_1,
    isCompletePropName: 'isOnSiteWaterTreatmentComplete',
    apiKey: 'calculation-on-site-water-treatment',
    title: i18n.t('business:Questions.calculations.onSiteWaterTreatment.title'),
    primaryI18nKey: 'business:QuestionPage.forms.onSiteWaterTreatment.key', // question selection step title
    subTextI18nKey: 'business:QuestionPage.forms.onSiteWaterTreatment.tip', // question selection step subtitle
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.onSiteWaterTreatment.key',
    icon: WaterTreatment,
    reductionPageKey: 'onSiteWaterTreatment',
    reductionPageChartTitle: i18n.t('business:QuestionPage.forms.onSiteWaterTreatment.key'),
    reductionPageCards: reductionCardsSlides,
    initiative: {
      getVisuals: ({ frontendEnum, questionTypeKey = null, isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: <>test</>,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          const { cardImage, headerImage } = initiativeVisuals(frontendEnum, questionTypeKey);
          return {
            description: <>test1</>,
            background: headerImage,
            useMask: false,
            cardBackground: cardImage,
          };
        }
      },
      formatForInitiativeSummary: ({ initiativeAnswerEnum = null } = {}) => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: true,
          category: initiativeAnswerEnum,
          description: `Remaining ${initiativeAnswerEnum} consumption for fuels burned on-site to reduce after incorporating the initiatives already added to the plan so far`,
          descriptionNoReduction: `${initiativeAnswerEnum} consumption for fuels burned on-site during the reporting period `,
        };
      },
    },
    table: {
      title: i18n.t('business:Questions.calculations.onSiteWaterTreatment.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.question3.table.heading1'),
        i18n.t('business:Questions.calculations.question3.table.heading2'),
        i18n.t('business:Questions.calculations.question3.table.heading3'),
      ],
      cells: [
        'fuelType',
        ({ quantity, fuelUnit = 'units', harmonisedInjection }: FuelQuantityRow) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)} ${harmonisedInjection(fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: false,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'fuelType',
      items: [
        'fuelType',
        ({ quantity, fuelUnit = 'units' }: Omit<FuelQuantityRow, 'harmonisedInjection'>) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question3.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with fuels used for on-site water treatment by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
        },
        {
          title: questionSelectorOptions.SUPPLIER,
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: getEducationContent(),
    },
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1({
        questionFormatName: 'on-site-water-treatment',
        emissionSourcePlaceholder: 'Select type of fuel',
      }),
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'on-site-water-treatment' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuels that have been used for on-site water treatment by your business during the reporting
          period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What fuels have been used for on-site water treatment by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
